'use client';

import { useEffect, useRef, useState, useMemo, useCallback, lazy, Suspense } from 'react';
import { Moon, RefreshCcw, Send, Sparkles, Sun, X, HelpCircle, Settings } from 'lucide-react';
import { FlowStep, Message, Sender, type UserPreferences, type QuickReplyAction } from './types';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import { generateRecommendation, generateRecommendationWithQuery, sendChatMessage } from './chatApi';
import { trackEvent } from './analytics';
import { getDreamBotRuntimeConfig } from './runtimeConfig';
import { useFocusTrap } from './useKeyboardNavigation';
import { saveConversation, loadConversation, clearConversation, hasStoredConversation, getConversationAge } from './conversationStorage';
import { ContinueConversationPrompt } from './ContinueConversationPrompt';
import { Confetti } from './Confetti';
import { DreamBotIcon } from './DreamBotIcon';
import { InputSuggestions, QuickStartSuggestions } from './InputSuggestions';
import { SkeletonMessage, SkeletonProductCard } from './SkeletonLoader';
import { LanguageSelector } from './LanguageSelector';
import { getUserProfile, getPersonalizedGreeting, markQuizCompleted, updateUserPreferences } from './personalization';
import { generateSmartQuickReplies, classifyIntent, deduplicateQuickReplies } from './quickReplyEngine';
import { useToast } from './useToast';
import { useOptimisticMessages } from './useOptimisticMessages';
import { ToastContainer } from './Toast';
import { hasCompletedOnboarding } from './Onboarding';
import { hasAcceptedPrivacy } from './PrivacyBanner';

// Lazy load heavy components for better initial load performance
const Onboarding = lazy(() => import('./Onboarding').then(m => ({ default: m.Onboarding })));
const PrivacySettings = lazy(() => import('./PrivacySettings').then(m => ({ default: m.PrivacySettings })));
 

const INITIAL_MESSAGE: Message = {
  id: 'init-1',
  text: "Welcome! I'm DreamBot. How do you usually sleep?",
  sender: Sender.BOT,
  timestamp: new Date(),
  quickReplies: [
    { label: 'Side Sleeper', value: 'Side Sleeper', nextFlowStep: FlowStep.PAIN_POINTS },
    { label: 'Back Sleeper', value: 'Back Sleeper', nextFlowStep: FlowStep.PAIN_POINTS },
    { label: 'Stomach Sleeper', value: 'Stomach Sleeper', nextFlowStep: FlowStep.PAIN_POINTS },
    { label: 'Combination', value: 'Combination Sleeper', nextFlowStep: FlowStep.PAIN_POINTS },
  ],
};

const KNOWN_BRANDS = ['Birch', 'Casper', 'Brooklyn Bedding', 'Leesa', 'Helix'];

const shouldRequestRecommendations = (message: string): boolean => {
  const lower = (message || '').toLowerCase();
  if (/\b(shipping|delivery|returns?|refund|trial|warranty|guarantee|financing|affirm)\b/i.test(lower)) {
    return false;
  }
  return /\b(recommend|suggest|best|top|which|compare|vs|versus|mattress|bed|sleep|pillow|pillows|sheet|sheets|bedding|protector|topper|toppers|duvet|comforter|blanket|frame|foundation|base|adjustable|accessor(?:y|ies))\b/i.test(lower);
};

const extractExplicitCategory = (message: string): string | null => {
  const lower = (message || '').toLowerCase();
  if (/\b(pillow|pillows)\b/.test(lower)) return 'pillow';
  if (/\b(mattress|mattresses|bed|beds)\b/.test(lower)) return 'mattress';
  if (/\b(sheet|sheets|bedding|duvet|comforter|blanket|protector|protectors)\b/.test(lower)) return 'bedding';
  if (/\b(frame|frames|foundation|foundations|base|bases|adjustable)\b/.test(lower)) return 'frame';
  return null;
};

const inferCategoryFromProducts = (products: any[]): string | null => {
  if (!Array.isArray(products) || products.length === 0) return null;

  const haystack = products
    .map((p) => {
      const features = Array.isArray(p?.features) ? p.features : [];
      const url = typeof p?.url === 'string' ? p.url : '';
      const name = typeof p?.name === 'string' ? p.name : '';
      return `${name} ${url} ${features.join(' ')}`.toLowerCase();
    })
    .join(' ');

  if (/\b(pillow|pillows)\b/.test(haystack)) return 'pillow';
  if (/\b(mattress|mattresses)\b/.test(haystack)) return 'mattress';
  if (/\b(sheet|sheets|bedding|duvet|comforter|blanket|protector|protectors)\b/.test(haystack)) return 'bedding';
  if (/\b(frame|frames|foundation|foundations|base|bases|adjustable)\b/.test(haystack)) return 'frame';
  return null;
};

const isCompareOrBestFollowup = (message: string): boolean => {
  const lower = (message || '').toLowerCase();
  return /\b(which one|which|best|better|compare|vs|versus)\b/.test(lower);
};

const formatStructuredChatText = (structured: any): string => {
  if (!structured || typeof structured !== 'object') return '';
  const parts: string[] = [];

  if (typeof structured.answer === 'string' && structured.answer.trim()) {
    parts.push(structured.answer.trim());
  }

  if (Array.isArray(structured.bullets) && structured.bullets.length > 0) {
    structured.bullets
      .filter((b: any) => typeof b === 'string')
      .map((b: string) => b.trim())
      .filter((b: string) => b.length > 0)
      .slice(0, 2)
      .forEach((b: string) => parts.push(`- ${b}`));
  }

  if (typeof structured.question === 'string' && structured.question.trim()) {
    parts.push(structured.question.trim());
  } else if (structured.next_step && typeof structured.next_step === 'object') {
    const label = typeof structured.next_step.label === 'string' ? structured.next_step.label.trim() : '';
    if (label) parts.push(label);
  }

  return parts.join('\n');
};

const buildProductAwareQuickReplies = (products: Array<{ name: string; price?: string }>): QuickReplyAction[] => {
  const topNames = products
    .map((p) => (typeof p?.name === 'string' ? p.name.trim() : ''))
    .filter((n) => n.length > 0)
    .slice(0, 3);

  const compareValue = topNames.length > 1
    ? `Compare these for me: ${topNames.join(' vs ')}`
    : 'Compare these options for me';

  return deduplicateQuickReplies([
    { label: 'Compare These', value: compareValue, nextFlowStep: FlowStep.OPEN_CHAT },
    { label: 'Firmer Options', value: 'Show me firmer, more supportive options', nextFlowStep: FlowStep.OPEN_CHAT },
    { label: 'Softer Options', value: 'Show me softer, more pressure-relieving options', nextFlowStep: FlowStep.OPEN_CHAT },
    { label: 'Cheaper Options', value: 'Show me more budget-friendly options', nextFlowStep: FlowStep.OPEN_CHAT },
  ]).slice(0, 4);
};

export function ChatWindow({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [flowStep, setFlowStep] = useState<FlowStep>(FlowStep.GREETING);
  const [userPrefs, setUserPrefs] = useState<UserPreferences>({});
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [showConfetti, setShowConfetti] = useState(false);
  const [scrollToMessageId, setScrollToMessageId] = useState<string | null>(null);
  const [conversationTopics, setConversationTopics] = useState<Set<string>>(new Set());
  const [policyQuestionCount, setPolicyQuestionCount] = useState(0);
  const [showContinuePrompt, setShowContinuePrompt] = useState(false);
  const [storedConversationAge, setStoredConversationAge] = useState<number | null>(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showPrivacyBanner, setShowPrivacyBanner] = useState(false);
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const toast = useToast();
  const optimistic = useOptimisticMessages(messages, setMessages);

  // Memoize bot messages for performance
  const botMessages = useMemo(() => 
    messages.filter(m => m.sender === Sender.BOT),
    [messages]
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const hasCheckedStorage = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipNextAutoScrollRef = useRef(false);
  const isNearBottomRef = useRef(true);
  const forceAutoScrollRef = useRef(false);

  // Focus trap for accessibility
  const focusTrapRef = useFocusTrap(true);

  useEffect(() => {
    if (!scrollToMessageId) return;

    // Prevent the next auto-scroll-to-bottom run after we clear the target.
    // Otherwise we scroll to the target, then immediately scroll to the bottom.
    skipNextAutoScrollRef.current = true;

    // Wait for DOM to fully render all product cards before scrolling
    const timeoutId = setTimeout(() => {
      const element = messageRefs.current.get(scrollToMessageId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setScrollToMessageId(null);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [messages, scrollToMessageId]);

  useEffect(() => {
    if (scrollToMessageId) return;
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      return;
    }

    const shouldAutoScroll = forceAutoScrollRef.current || isNearBottomRef.current;
    if (!shouldAutoScroll) return;

    const last = messages[messages.length - 1];
    if (last && last.sender === Sender.BOT) {
      const el = messageRefs.current.get(last.id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    forceAutoScrollRef.current = false;
  }, [messages, isTyping, scrollToMessageId]);

  // Announce new messages to screen readers
  useEffect(() => {
    if (messages.length > 0 && liveRegionRef.current) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === Sender.BOT) {
        liveRegionRef.current.textContent = `DreamBot says: ${lastMessage.text}`;
      }
    }
  }, [messages]);

  // Check for stored conversation and onboarding on mount
  useEffect(() => {
    if (!hasCheckedStorage.current) {
      hasCheckedStorage.current = true;
      
      // Onboarding disabled by default - users can access via help button
      // if (!hasCompletedOnboarding()) {
      //   setShowOnboarding(true);
      //   trackEvent('onboarding_shown', { first_time_user: true });
      // } else 
      // Privacy banner disabled by default
      // if (!hasAcceptedPrivacy()) {
      //   setShowPrivacyBanner(true);
      // } else 
      if (hasStoredConversation()) {
        const age = getConversationAge();
        if (age !== null) {
          setStoredConversationAge(age);
          setShowContinuePrompt(true);
          trackEvent('stored_conversation_detected', { age_hours: age });
        }
      }
    }

    const { mountId } = getDreamBotRuntimeConfig();
    const id = mountId && typeof mountId === 'string' ? mountId : 'dreambot-widget-root';
    const el = document.getElementById(id);
    const isDark = !!el?.classList.contains('dark');
    setTheme(isDark ? 'dark' : 'light');
  }, []);

  // Auto-save conversation on changes (debounced)
  useEffect(() => {
    if (messages.length > 1 && !showContinuePrompt) {
      const timer = setTimeout(() => {
        saveConversation(messages, flowStep, userPrefs, sessionId);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [messages, flowStep, userPrefs, sessionId, showContinuePrompt]);

  const handleContinueConversation = () => {
    const stored = loadConversation();
    if (stored) {
      setMessages(stored.messages);
      setFlowStep(stored.flowStep);
      setUserPrefs(stored.userPrefs);
      setConversationTopics(new Set((stored as any).conversationTopics || []));
      setPolicyQuestionCount((stored as any).policyQuestionCount || 0);
      setShowContinuePrompt(false);
      trackEvent('conversation_continued', { messageCount: stored.messages.length });
      // Toast disabled by default
      // toast.info('Conversation restored');
    }
  };

  const handleStartNewConversation = () => {
    clearConversation();
    setShowContinuePrompt(false);
    setMessages([INITIAL_MESSAGE]);
    setFlowStep(FlowStep.GREETING);
    setUserPrefs({});
    setConversationTopics(new Set());
    setPolicyQuestionCount(0);
    trackEvent('conversation_restarted', { trigger: 'new_conversation_prompt' });
    // Toast disabled by default
    // toast.success('Started a fresh conversation');
  };

  const handleRestart = () => {
    clearConversation();
    setMessages([INITIAL_MESSAGE]);
    setFlowStep(FlowStep.GREETING);
    setUserPrefs({});
    setIsTyping(false);
    trackEvent('conversation_restarted', { trigger: 'restart_button' });
  };

  const handleToggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);

    try {
      localStorage.setItem('dreambot_theme', next);
    } catch {
      // ignore
    }

    const { mountId } = getDreamBotRuntimeConfig();
    const id = mountId && typeof mountId === 'string' ? mountId : 'dreambot-widget-root';
    const el = document.getElementById(id);
    if (el) {
      if (next === 'dark') el.classList.add('dark');
      else el.classList.remove('dark');
    }
  };

  const handleSendMessage = async (text: string, nextStep?: FlowStep, displayText?: string) => {
    if (!text.trim()) return;

    const actionLabel = typeof displayText === 'string' ? displayText : '';

    // 1. Handle Restart immediately
    if (text === 'Restart') {
      trackEvent('quiz_restart');
      setMessages([INITIAL_MESSAGE]);
      setFlowStep(FlowStep.GREETING);
      setUserPrefs({});
      setIsTyping(false);
      return;
    }

    if (actionLabel === 'Compare These') {
      const latest = [...messages].reverse().find((m: any) => Array.isArray(m?.productRecommendations) && m.productRecommendations.length > 1) as any;
      const products = latest?.productRecommendations as any[] | undefined;
      if (Array.isArray(products) && products.length > 1) {
        const userMsg: Message = {
          id: Date.now().toString(),
          text: actionLabel,
          sender: Sender.USER,
          timestamp: new Date(),
        };

        forceAutoScrollRef.current = true;
        setMessages((prev) => [...prev, userMsg]);
        setInput('');
        setIsTyping(false);

        const lines: string[] = [];
        lines.push("Here’s a quick comparison of the options you’re looking at:");

        products.slice(0, 3).forEach((p) => {
          const name = typeof p?.name === 'string' ? p.name : 'Product';
          const price = typeof p?.price === 'string' ? p.price : 'See Price';
          const feat = Array.isArray(p?.features) ? p.features.filter((f: any) => typeof f === 'string').slice(0, 2) : [];
          const featText = feat.length > 0 ? ` — ${feat.join(', ')}` : '';
          lines.push(`- ${name} (${price})${featText}`);
        });

        lines.push('What matters most for you: cooling feel, plushness, or support?');

        const botMsg: Message = {
          id: (Date.now() + 1).toString(),
          text: lines.join('\n'),
          sender: Sender.BOT,
          timestamp: new Date(),
          productRecommendations: products.slice(0, 3),
          quickReplies: [
            { label: 'Cooling Feel', value: 'Cooling is my top priority', nextFlowStep: FlowStep.OPEN_CHAT },
            { label: 'Plush & Soft', value: 'I want something softer/plusher', nextFlowStep: FlowStep.OPEN_CHAT },
            { label: 'Support', value: 'I need more support', nextFlowStep: FlowStep.OPEN_CHAT },
            { label: 'View More', value: 'Show me more options like these', nextFlowStep: FlowStep.OPEN_CHAT },
          ],
        };

        trackEvent('compare_shown', { count: products.length });
        setMessages((prev) => [...prev, botMsg]);
        return;
      }
    }

    const userMsg: Message = {
      id: Date.now().toString(),
      text: displayText || text,
      sender: Sender.USER,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    forceAutoScrollRef.current = true;

    const isOpenChatMessage = flowStep === FlowStep.OPEN_CHAT || nextStep === FlowStep.OPEN_CHAT;
    if (isOpenChatMessage) {
      trackEvent('open_chat_message_sent', { hasHistory: messages.length > 0 });
    }

    if (nextStep && nextStep !== FlowStep.OPEN_CHAT) {
      const normalized = (() => {
        const t = text.toLowerCase();

        if (flowStep === FlowStep.GREETING) {
          if (t.includes('side')) return 'side';
          if (t.includes('back')) return 'back';
          if (t.includes('stomach')) return 'stomach';
          if (t.includes('combination')) return 'combo';
          return 'custom';
        }

        if (flowStep === FlowStep.PAIN_POINTS) {
          if (t.includes('lower') && t.includes('back')) return 'lower_back';
          if (t.includes('shoulder')) return 'shoulder';
          if (t.includes('overheat') || t.includes('hot')) return 'overheating';
          if (t.includes('no pain')) return 'no_pain';
          return 'custom';
        }

        if (flowStep === FlowStep.FIRMNESS) {
          if (t.includes('soft')) return 'soft';
          if (t.includes('medium')) return 'medium';
          if (t.includes('firm')) return 'firm';
          return 'custom';
        }

        if (flowStep === FlowStep.BUDGET) {
          if (t.includes('budget')) return 'budget';
          if (t.includes('standard')) return 'standard';
          if (t.includes('luxury')) return 'luxury';
          return 'custom';
        }

        if (flowStep === FlowStep.BED_SIZE) {
          if (t.includes('twin xl')) return 'twin_xl';
          if (t.includes('twin')) return 'twin';
          if (t.includes('full')) return 'full';
          if (t.includes('queen')) return 'queen';
          if (t.includes('cal') && t.includes('king')) return 'cal_king';
          if (t.includes('king')) return 'king';
          return 'custom';
        }

        return 'custom';
      })();

      trackEvent('quiz_answer', { step: flowStep, choice: normalized });
    }

    // 2. Handle Quiz Steps (Simulated Bot Response)
    if (nextStep && nextStep !== FlowStep.OPEN_CHAT) {
      const newPrefs = { ...userPrefs };
      if (flowStep === FlowStep.GREETING) newPrefs.position = text;
      if (flowStep === FlowStep.PAIN_POINTS) newPrefs.pain = text;
      if (flowStep === FlowStep.FIRMNESS) newPrefs.firmness = text;
      if (flowStep === FlowStep.BUDGET) newPrefs.budget = text;
      if (flowStep === FlowStep.BED_SIZE) newPrefs.bedSize = text;
      setUserPrefs(newPrefs);

      setFlowStep(nextStep);

      setTimeout(async () => {
        let botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: '',
          sender: Sender.BOT,
          timestamp: new Date(),
        };

        if (nextStep === FlowStep.PAIN_POINTS) {
          botResponse.text = 'Got it. Do you experience any specific discomfort when waking up?';
          botResponse.quickReplies = [
            { label: 'Lower Back Pain', value: 'Lower Back Pain', nextFlowStep: FlowStep.FIRMNESS },
            { label: 'Shoulder Pain', value: 'Shoulder Pain', nextFlowStep: FlowStep.FIRMNESS },
            { label: 'Runs Hot', value: 'Overheating', nextFlowStep: FlowStep.FIRMNESS },
            { label: 'No Pain', value: 'No Pain', nextFlowStep: FlowStep.FIRMNESS },
          ];
        } else if (nextStep === FlowStep.FIRMNESS) {
          botResponse.text = 'Understood. When it comes to feel, what do you usually prefer?';
          botResponse.quickReplies = [
            { label: 'Cloud-like Soft', value: 'Soft', nextFlowStep: FlowStep.BUDGET },
            { label: 'Perfect Medium', value: 'Medium', nextFlowStep: FlowStep.BUDGET },
            { label: 'Supportive Firm', value: 'Firm', nextFlowStep: FlowStep.BUDGET },
          ];
        } else if (nextStep === FlowStep.BUDGET) {
          botResponse.text = 'What is your approximate budget?';
          botResponse.quickReplies = [
            { label: 'Under $1000', value: 'Budget-Conscious', nextFlowStep: FlowStep.BED_SIZE },
            { label: '$1000 - $1600', value: 'Standard', nextFlowStep: FlowStep.BED_SIZE },
            { label: 'Price is no object', value: 'Luxury', nextFlowStep: FlowStep.BED_SIZE },
          ];
        } else if (nextStep === FlowStep.BED_SIZE) {
          botResponse.text = 'Great. What bed size are you shopping for?';
          botResponse.quickReplies = [
            { label: 'Twin', value: 'Twin', nextFlowStep: FlowStep.RECOMMENDATION },
            { label: 'Twin XL', value: 'Twin XL', nextFlowStep: FlowStep.RECOMMENDATION },
            { label: 'Full', value: 'Full', nextFlowStep: FlowStep.RECOMMENDATION },
            { label: 'Queen', value: 'Queen', nextFlowStep: FlowStep.RECOMMENDATION },
            { label: 'King', value: 'King', nextFlowStep: FlowStep.RECOMMENDATION },
            { label: 'Cal King', value: 'Cal King', nextFlowStep: FlowStep.RECOMMENDATION },
          ];
        } else if (nextStep === FlowStep.RECOMMENDATION) {
          try {
            const res: any = await generateRecommendation(newPrefs);
            const data = res && typeof res === 'object'
              ? (typeof res.json === 'string' ? JSON.parse(res.json) : res)
              : null;
            const recCount = data && Array.isArray(data.recommendations) ? data.recommendations.length : 0;

            const quickReplies: QuickReplyAction[] = [
              { label: 'Ask about Warranty', value: 'Tell me about the warranty', nextFlowStep: FlowStep.OPEN_CHAT },
              { label: 'Shipping Info', value: 'How does shipping work?', nextFlowStep: FlowStep.OPEN_CHAT },
              { label: 'Start Over', value: 'Restart', nextFlowStep: FlowStep.GREETING },
            ];

            const fallbackImage =
              'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&q=80&w=600';

            // Backward compatible: old payload is a single product object
            if (data && Array.isArray(data.recommendations) && data.recommendations.length > 0) {
              trackEvent('recommendations_shown', { source: 'quiz', count: Math.min(3, data.recommendations.length) });
              if (data.recommendations.length < 3) {
                trackEvent('recommendations_insufficient', { source: 'quiz', count: data.recommendations.length });
              }
              const summaryText = typeof data.summary === 'string' && data.summary.trim() ? data.summary : "Here are my top picks.";

              const toPrice = (p: any) => (typeof p === 'string' && p.trim() ? p.trim() : 'See Price');

              const productRecommendations = data.recommendations
                .slice(0, 3)
                .map((rec: any) => ({
                  name: rec?.name || 'Recommended Product',
                  price: toPrice(rec?.price),
                  reasoning: rec?.reasoning || '',
                  description: rec?.description || '',
                  features: Array.isArray(rec?.features) ? rec.features : [],
                  imageUrl: (rec && rec.imageUrl) || fallbackImage,
                  url: rec?.url,
                }))
                .filter((p: any) => typeof p?.name === 'string' && p.name.trim().length > 0);

              const msgId = (Date.now() + 1).toString();
              const msg: Message = {
                id: msgId,
                text: summaryText,
                sender: Sender.BOT,
                timestamp: new Date(),
                productRecommendations,
                quickReplies,
              };

              // Set scroll target BEFORE adding messages to prevent auto-scroll to bottom
              setScrollToMessageId(msgId);
              setMessages((prev) => [...prev, msg]);
              setIsTyping(false);

              // Celebrate with confetti!
              setTimeout(() => setShowConfetti(true), 500);
              setTimeout(() => setShowConfetti(false), 3500);

              
              return;
            }

            // Use the real image from the API, or a high-quality fallback
            let imageUrl = data?.imageUrl;
            if (!imageUrl) {
              imageUrl = fallbackImage;
            }

            botResponse.text = "I've analyzed your sleep profile. Here is your perfect match.";
            botResponse.productDetails = {
              name: data.name,
              price: data.price,
              reasoning: data.reasoning,
              features: data.features || [],
              imageUrl,
              url: data.url,
            };

            botResponse.quickReplies = quickReplies;

            trackEvent('recommendations_shown', { source: 'quiz', count: 1 });
            
            // Celebrate with confetti!
            setTimeout(() => setShowConfetti(true), 500);
            setTimeout(() => setShowConfetti(false), 3500);
          } catch (e) {
            botResponse.text = "I recommend our Hybrid Core mattress. It's our most versatile option!";
          }
        }

        setMessages((prev) => [...prev, botResponse]);
        setIsTyping(false);
      }, 800);

      return;
    }

    // 3. Handle Open Chat AI Call
    if (nextStep === FlowStep.OPEN_CHAT) {
      setFlowStep(FlowStep.OPEN_CHAT);
    }

    try {
      const history = messages.map((m: any) => {
        const role = (m.sender === Sender.USER ? 'user' : 'model') as 'user' | 'model';
        let t = typeof m.text === 'string' ? m.text : '';

        if (role === 'model') {
          const products: any[] = Array.isArray(m?.productRecommendations) ? m.productRecommendations : (m?.productDetails ? [m.productDetails] : []);
          if (products.length > 0) {
            const productLines = products.slice(0, 3).map((p) => {
              const name = typeof p?.name === 'string' ? p.name : '';
              if (!name) return '';
              const desc = typeof p?.description === 'string' ? p.description : '';
              const features = Array.isArray(p?.features) ? p.features.filter((f: any) => typeof f === 'string').slice(0, 4) : [];
              const bits = [desc ? `Description: ${desc}` : '', features.length > 0 ? `Features: ${features.join(', ')}` : '']
                .filter(Boolean)
                .join(' | ');
              return bits ? `- ${name} — ${bits}` : `- ${name}`;
            }).filter(Boolean);

            if (productLines.length > 0) {
              t = `${t}\nProducts shown:\n${productLines.join('\n')}`;
            }
          }
        }

        return { role, text: t };
      });

      const fallbackImage = 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&q=80&w=600';

      const mapToProductDetails = (p: any) => {
        const name = typeof p?.name === 'string' ? p.name : '';
        if (!name) return null;
        const imageUrl = typeof p?.imageUrl === 'string' && p.imageUrl
          ? p.imageUrl
          : (typeof p?.image_url === 'string' && p.image_url ? p.image_url : fallbackImage);

        return {
          name,
          price: typeof p?.price === 'string' && p.price ? p.price : 'See Price',
          reasoning: typeof p?.reasoning === 'string' && p.reasoning ? p.reasoning : 'Recommended based on your needs.',
          description: typeof p?.description === 'string' ? p.description : undefined,
          features: Array.isArray(p?.features) ? p.features.filter((f: any) => typeof f === 'string').slice(0, 6) : [],
          imageUrl,
          url: typeof p?.url === 'string' ? p.url : undefined,
        };
      };

      let productRecommendations: any[] = [];
      const latestProductMsg = [...messages].reverse().find((m: any) => Array.isArray(m?.productRecommendations) && m.productRecommendations.length > 0) as any;
      const latestProducts = Array.isArray(latestProductMsg?.productRecommendations) ? latestProductMsg.productRecommendations : [];
      const explicitCategory = extractExplicitCategory(text);
      const followupReuse = !explicitCategory && latestProducts.length > 0 && isCompareOrBestFollowup(text);

      const shouldPrefetch = shouldRequestRecommendations(text);
      if (shouldPrefetch) {
        try {
          if (followupReuse) {
            productRecommendations = latestProducts.slice(0, 3).map(mapToProductDetails).filter(Boolean);
          } else {
            const inferredCategory = explicitCategory || inferCategoryFromProducts(latestProducts);
            const recQuery = inferredCategory && !explicitCategory ? `${inferredCategory} ${text}` : text;
            const res: any = await generateRecommendationWithQuery(userPrefs, recQuery);
            const data = res && typeof res === 'object'
              ? (typeof res.json === 'string' ? JSON.parse(res.json) : res)
              : null;

            if (data && Array.isArray(data.recommendations) && data.recommendations.length > 0) {
              productRecommendations = data.recommendations
                .slice(0, 3)
                .map(mapToProductDetails)
                .filter(Boolean);
            }
          }
        } catch {
          // ignore - prefetch is a best-effort optimization
        }
      }

      const storeContextLines = productRecommendations
        .slice(0, 3)
        .map((p: any) => {
          const name = typeof p?.name === 'string' ? p.name : '';
          if (!name) return '';
          const desc = typeof p?.description === 'string' ? p.description : '';
          const features = Array.isArray(p?.features) ? p.features.filter((f: any) => typeof f === 'string').slice(0, 5) : [];
          const bits = [desc ? `Description: ${desc}` : '', features.length > 0 ? `Features/Tags: ${features.join(', ')}` : '']
            .filter(Boolean)
            .join(' | ');
          return bits ? `- ${name} — ${bits}` : `- ${name}`;
        })
        .filter(Boolean);

      const historyForAi = storeContextLines.length > 0
        ? [...history, { role: 'model' as const, text: `Store context (from live catalog search):\n${storeContextLines.join('\n')}` }]
        : history;

      const { text: responseText, structured } = await sendChatMessage(text, historyForAi) as any;

      const displayedText = formatStructuredChatText(structured) || responseText;

      // Fallback: if Worker attached structured recommended_products, use those
      if (productRecommendations.length === 0) {
        const structuredRecommendations: any[] = Array.isArray(structured?.recommended_products)
          ? structured.recommended_products
          : [];

        productRecommendations = structuredRecommendations
          .slice(0, 3)
          .map(mapToProductDetails)
          .filter(Boolean);
      }

      if (shouldRequestRecommendations(text)) {
        if (productRecommendations.length > 0) {
          trackEvent('recommendations_shown', { source: 'open_chat', count: productRecommendations.length });
        }
        if (productRecommendations.length < 3) {
          trackEvent('recommendations_insufficient', { source: 'open_chat', count: productRecommendations.length });
        }
      }

      // Track conversation context
      const intent = classifyIntent(text);
      setConversationTopics(prev => new Set(prev).add(intent));
      
      if (['shipping', 'warranty', 'returns'].includes(intent)) {
        setPolicyQuestionCount(prev => prev + 1);
      }

      // --- SMART BUTTON LOGIC (NEW ENGINE) ---
      const context = {
        messageCount: messages.length,
        topicsDiscussed: conversationTopics,
        lastUserIntent: intent,
        hasSeenRecommendations: messages.some(m => m.productDetails || (Array.isArray((m as any).productRecommendations) && (m as any).productRecommendations.length > 0)),
        policyQuestionsAsked: policyQuestionCount,
      };

      const structuredQuickReplies: any[] = Array.isArray(structured?.quick_replies)
        ? structured.quick_replies
        : [];

      const allowStructuredQuickReplies = ['shipping', 'warranty', 'returns', 'pricing'].includes(intent);
      const usableStructuredQuickReplies = allowStructuredQuickReplies ? structuredQuickReplies : [];

      const baseQuickReplies = usableStructuredQuickReplies.length > 0
        ? usableStructuredQuickReplies.slice(0, 4).map((r) => ({
            label: typeof r.label === 'string' ? r.label : '',
            value: typeof r.value === 'string' ? r.value : '',
            nextFlowStep: r.next_flow_step === 'greeting' ? FlowStep.GREETING : FlowStep.OPEN_CHAT,
          })).filter((r) => r.label && r.value)
        : generateSmartQuickReplies(
            displayedText,
            text,
            context,
            KNOWN_BRANDS
          );

      const quickReplies = (usableStructuredQuickReplies.length === 0 && productRecommendations.length > 0)
        ? deduplicateQuickReplies([
            ...buildProductAwareQuickReplies(productRecommendations as any),
            ...baseQuickReplies,
          ]).slice(0, 4)
        : baseQuickReplies;

      const botMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: displayedText || "I'm having a little trouble connecting. Try again?",
        sender: Sender.BOT,
        timestamp: new Date(),
        quickReplies: quickReplies,
        productRecommendations: productRecommendations.length > 0 ? (productRecommendations as any) : undefined,
      };

      setMessages((prev) => [...prev, botMsg]);
    } catch (e) {
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: "I'm sorry, I encountered a temporary error. Please try asking again.",
        sender: Sender.BOT,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  // Combine refs for focus trap and swipe gesture
  const combinedRef = (el: HTMLDivElement | null) => {
    if (focusTrapRef) {
      (focusTrapRef as any).current = el;
    }
  };

  return (
    <div 
      ref={combinedRef}
      id="dreambot-chat-window"
      role="dialog"
      aria-label="DreamBot AI Sleep Concierge Chat"
      aria-modal="true"
      className="flex flex-col h-full bg-transparent text-card-foreground relative"
    >
      {/* Continue conversation prompt overlay */}
      {showContinuePrompt && storedConversationAge !== null && (
        <ContinueConversationPrompt
          conversationAge={storedConversationAge}
          onContinue={handleContinueConversation}
          onStartNew={handleStartNewConversation}
        />
      )}

      {/* Confetti celebration */}
      <Confetti active={showConfetti} duration={3000} />

      {/* Toast notifications */}
      <ToastContainer toasts={toast.toasts} onClose={toast.dismiss} />

      {/* Onboarding tutorial for first-time users */}
      {showOnboarding && (
        <Suspense fallback={<div className="absolute inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center"><TypingIndicator /></div>}>
          <Onboarding
            onComplete={() => {
              setShowOnboarding(false);
              toast.success('Welcome! Let\'s find your perfect mattress');
              // Show privacy banner after onboarding
              if (!hasAcceptedPrivacy()) {
                setTimeout(() => setShowPrivacyBanner(true), 1000);
              }
            }}
            onSkip={() => {
              setShowOnboarding(false);
              // Show privacy banner after skipping
              if (!hasAcceptedPrivacy()) {
                setTimeout(() => setShowPrivacyBanner(true), 1000);
              }
            }}
          />
        </Suspense>
      )}

      {/* Privacy settings panel */}
      {showPrivacySettings && (
        <Suspense fallback={<div className="absolute inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center"><TypingIndicator /></div>}>
          <PrivacySettings
            onClose={() => setShowPrivacySettings(false)}
          />
        </Suspense>
      )}

      {/* Screen reader live region for announcements */}
      <div
        ref={liveRegionRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />

      <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none">
        <div className="absolute inset-0 h-24 floating-header-gradient"></div>
        <div className="relative h-16 px-4 pt-3 flex items-center justify-between pointer-events-auto">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0">
              <DreamBotIcon size={32} />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground leading-none text-[15px] tracking-[-0.015em] truncate">DreamBot</h3>
              <p className="text-[12px] font-normal text-muted-foreground leading-none mt-1 tracking-[-0.011em] truncate">AI Sleep Concierge</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0 items-center">
            {(() => {
              const config = getDreamBotRuntimeConfig();
              return (
                <>
                  {config.showLanguageSelector && <LanguageSelector />}
                  {config.showSettingsButton && (
                    <button
                      onClick={() => {
                        setShowPrivacySettings(true);
                        trackEvent('privacy_settings_opened');
                      }}
                      aria-label="Privacy settings"
                      title="Privacy settings"
                      className="p-1.5 text-muted-foreground bg-transparent hover:bg-transparent"
                    >
                      <Settings size={18} />
                    </button>
                  )}
                  {config.showTutorialButton && (
                    <button
                      onClick={() => {
                        setShowOnboarding(true);
                        trackEvent('help_button_clicked');
                      }}
                      aria-label="Show tutorial"
                      title="Show tutorial"
                      className="p-1.5 text-muted-foreground bg-transparent hover:bg-transparent"
                    >
                      <HelpCircle size={18} />
                    </button>
                  )}
                </>
              );
            })()}
            <button
              onClick={handleToggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors duration-200"
              style={{ background: 'none', border: 'none', boxShadow: 'none' }}
            >
              {theme === 'dark' ? <Sun size={20} className="text-amber-400" /> : <Moon size={20} className="text-primary" />}
            </button>
            <button
              onClick={() => handleSendMessage('Restart')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors duration-200"
              title="Restart Chat"
              style={{ background: 'none', border: 'none', boxShadow: 'none' }}
            >
              <RefreshCcw size={20} />
            </button>
            <button
              onClick={onClose}
              aria-label="Close chat"
              title="Close chat (Ctrl+K)"
              className="p-2 text-muted-foreground hover:text-foreground transition-colors duration-200"
              style={{ background: 'none', border: 'none', boxShadow: 'none' }}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
          isNearBottomRef.current = remaining < 96;
        }}
        className="flex-1 overflow-y-auto bg-background px-3 pt-20 pb-24 space-y-4 scroll-smooth custom-scrollbar"
      >
        {messages.map((msg, index) => (
          <div
            key={msg.id}
            ref={(el) => {
              if (el) {
                messageRefs.current.set(msg.id, el);
              } else {
                messageRefs.current.delete(msg.id);
              }
            }}
          >
            <MessageBubble
              message={msg}
              isLast={index === messages.length - 1}
              onReply={handleSendMessage}
            />
          </div>
        ))}

        {isTyping && (
          <div className="space-y-4">
            {flowStep === FlowStep.RECOMMENDATION ? (
              <>
                <SkeletonMessage />
                <SkeletonProductCard />
              </>
            ) : (
              <TypingIndicator />
            )}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick start suggestions for new conversations - only show if no quick replies */}
      {messages.length === 1 && !input && flowStep === FlowStep.GREETING && !messages[0].quickReplies && (
        <QuickStartSuggestions
          onSelect={(text) => {
            setInput(text);
            inputRef.current?.focus();
            trackEvent('quick_start_selected', { text });
          }}
        />
      )}

      {/* Input Area - Ultra Modern Floating Transparent Composer */}
      <div className="floating-composer-container absolute left-0 right-0 bottom-0 px-4 pb-2 pt-2 bg-transparent" style={{ background: 'transparent', backgroundColor: 'transparent' }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage(input);
            setShowSuggestions(false);
          }}
        >
          <div className="floating-composer-surface flex items-center gap-3 relative">
            {/* Input suggestions */}
            <InputSuggestions
              input={input}
              onSelect={(text) => {
                setInput(text);
                setShowSuggestions(false);
                inputRef.current?.focus();
                trackEvent('suggestion_selected', { text });
              }}
              isVisible={showSuggestions && input.length >= 2}
            />

            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSendMessage(input);
                  setShowSuggestions(false);
                } else if (e.key === 'Escape') {
                  setShowSuggestions(false);
                }
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Ask about sleep, pain, or products..."
              aria-label="Chat message input"
              aria-describedby="chat-input-description"
              className="flex-1 text-foreground placeholder:text-muted-foreground/60 px-4 py-3.5 text-[15px] font-normal tracking-[-0.011em] min-w-0 floating-composer-input"
              style={{
                background: 'transparent',
                backgroundColor: 'transparent',
                border: 'none',
                borderWidth: 0,
                borderStyle: 'none',
                outline: 'none',
                outlineWidth: 0,
                outlineStyle: 'none',
                boxShadow: 'none',
              }}
              disabled={isTyping}
            />
            <span id="chat-input-description" className="sr-only">
              Type your message and press Enter to send
            </span>
            <button
              onClick={() => {
                handleSendMessage(input);
                setShowSuggestions(false);
              }}
              disabled={!input.trim() || isTyping}
              aria-label="Send message"
              title="Send message (or press Enter)"
              className="composer-send-button bg-transparent text-foreground disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 hover:opacity-70 transition-all duration-200"
            >
              <Send size={20} strokeWidth={2.5} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
